import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Advice, Button, Markdown, Modal, Progress, Tabs, Text } from '@Sage/hoi-poi-ui';

import { EntityListActions, EntityListSelectActions, ServerListActions } from 'actions';
import AbsoluteLoader from 'components/AbsoluteLoader';
import { COMPANIES } from 'constants/Entities';
import { subscribe } from 'lib/EventBuser';
import { LEADS_MODAL_ACCOUNT_CREATED } from 'lib/events';
import { LeadsService } from 'services';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getLang } from 'utils/literals';
import { getBrowserGeolocation, resolveMapFallBackCenterFromUserData } from 'utils/map';

import LeadList from './LeadList';
import LeadsMap from './LeadsMap';
import LeadsQuickFilters from './LeadsQuickFilters';
import PlaceSearch from './PlaceSearch';
import ReviewQueue from './ReviewQueue';
import LeadPopEnrichingPanel from './LeadPopEnrichingPanel';
import {
    LEADS_MODAL_STEPS,
    LEADS_PREVIEW_ENTITY,
    MAX_RESULT_OPTIONS,
    RADIUS_OPTIONS,
    RESULT_BUCKETS,
} from './constants';
import { buildLeadPopLiterals } from './helpers/leadPopLiterals';
import {
    buildPreviewRequest,
    getSelectedLeads,
    markReviewRowAsCreated,
    splitByBucket,
    toAccountCrudData,
} from './helpers';
import ReviewSummary from './ReviewSummary';

import './styles.scss';

const EM_DASH = '\u2014';

const DEFAULT_ORIGIN = { lat: 41.391067, lng: 2.1288348 };
const DEFAULT_CATEGORY = '';
const DEFAULT_MAX_RESULTS = MAX_RESULT_OPTIONS[1].value;
const DEFAULT_RADIUS_KM = RADIUS_OPTIONS[1].value;
const getDefaultOrigin = () => ({ ...DEFAULT_ORIGIN });
const getDefaultProgress = () => ({ current: 0, total: 0 });

const isFiniteNumber = (value) => Number.isFinite(Number(value));
const isValidCoordinate = (value, min, max) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= min && number <= max;
};

const LeadsModal = ({ isOpen, onClose, onReviewCreate }) => {
    const dispatch = useDispatch();
    const countries = useSelector((state) => state.serverList?.tblCountries?.data || []);
    const userLatitude = useSelector((state) => state.config.userData?.userLatitude);
    const userLongitude = useSelector((state) => state.config.userData?.userLongitude);
    const defaultCountryId = useSelector((state) => state.config.userData?.defaultCountryId);
    const companiesMapLastPosition = useSelector(
        (state) => state.entityMap?.[COMPANIES.entity]?.lastPosition ?? null,
    );
    const companiesMapLastPositionRef = useRef(companiesMapLastPosition);
    companiesMapLastPositionRef.current = companiesMapLastPosition;
    const countriesRef = useRef(countries);
    countriesRef.current = countries;

    const [step, setStep] = useState(LEADS_MODAL_STEPS.SETUP);
    const [category, setCategory] = useState(DEFAULT_CATEGORY);
    const [maxResults, setMaxResults] = useState(DEFAULT_MAX_RESULTS);
    const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
    const [origin, setOrigin] = useState(null);
    /** False until the open-session origin resolution chain has finished (avoids flashing the hardcoded default). */
    const [isOriginReady, setIsOriginReady] = useState(false);
    /** First resolved search origin this session; “back to start” target on the map. */
    const [initialOrigin, setInitialOrigin] = useState(null);
    const [leads, setLeads] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [activeBucket, setActiveBucket] = useState(RESULT_BUCKETS.NEW);
    const [creditBalance, setCreditBalance] = useState(null);
    const [confirmCreditsOpen, setConfirmCreditsOpen] = useState(false);
    const [businessTypes, setBusinessTypes] = useState([]);
    const [businessTypesLoading, setBusinessTypesLoading] = useState(false);
    const [businessTypesError, setBusinessTypesError] = useState(false);
    const [reviewRows, setReviewRows] = useState([]);
    const [progress, setProgress] = useState(getDefaultProgress);
    /** True when enrichment finished but user has not opened review yet. */
    const [enrichmentReady, setEnrichmentReady] = useState(false);
    /** Append-only queue; batched SSE chunks must not use a single signal state. */
    const [backendLeadQueue, setBackendLeadQueue] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    /** False until Google Maps has mounted in LeadsMap (keeps prospect disabled if the map never loads). */
    const [isMapReady, setIsMapReady] = useState(false);
    const isOpenRef = useRef(isOpen);
    const isClosingRef = useRef(!isOpen);
    const requestVersionRef = useRef(0);

    const beginClose = useCallback(() => {
        if (!isClosingRef.current) {
            requestVersionRef.current += 1;
        }
        isClosingRef.current = true;
    }, []);

    const isRequestCurrent = useCallback(
        (requestVersion) =>
            isOpenRef.current &&
            !isClosingRef.current &&
            requestVersion === requestVersionRef.current,
        [],
    );

    const resetLeadsPreviewSelection = useCallback(() => {
        dispatch(EntityListSelectActions.resetListSelect(LEADS_PREVIEW_ENTITY, true));
        dispatch(EntityListSelectActions.setShouldClearWeb5Checkboxes(LEADS_PREVIEW_ENTITY, true));
    }, [dispatch]);

    useEffect(() => {
        isOpenRef.current = isOpen;

        if (isOpen) {
            isClosingRef.current = false;
            setSelectedIds([]);
            resetLeadsPreviewSelection();
            return;
        }

        beginClose();
    }, [beginClose, isOpen, resetLeadsPreviewSelection]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const requestVersion = requestVersionRef.current;
        setBusinessTypesLoading(true);
        setBusinessTypesError(false);

        LeadsService.getBusinessTypes()
            .then((types) => {
                if (!isRequestCurrent(requestVersion)) return;
                setBusinessTypes(types);
                if (!types.length) {
                    setBusinessTypesError(true);
                }
            })
            .catch((err) => {
                if (!isRequestCurrent(requestVersion) || LeadsService.isCanceledRequest(err)) {
                    return;
                }
                setBusinessTypes([]);
                setBusinessTypesError(true);
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setBusinessTypesLoading(false);
                }
            });

        LeadsService.getCredits()
            .then(({ balance }) => {
                if (!isRequestCurrent(requestVersion)) return;
                setCreditBalance(balance);
            })
            .catch(() => {
                if (!isRequestCurrent(requestVersion)) return;
                setCreditBalance(null);
            });

        return undefined;
    }, [isOpen, isRequestCurrent]);

    /** Same resolution order as `components/Map`: browser → profile lat/lng → country geocode → companies map last center → default. */
    useEffect(() => {
        if (!isOpen) return undefined;

        let cancelled = false;
        setOrigin(null);
        setInitialOrigin(null);
        setIsOriginReady(false);
        setIsMapReady(false);

        const applyOriginIfValid = (candidate) => {
            if (cancelled || !isOpenRef.current) return false;
            if (candidate == null) return false;
            const lat = Number(candidate.lat);
            const lng = Number(candidate.lng);
            if (!isValidCoordinate(lat, -90, 90) || !isValidCoordinate(lng, -180, 180))
                return false;
            const next = { lat, lng };
            setOrigin(next);
            setInitialOrigin((current) => current ?? next);
            return true;
        };

        (async () => {
            try {
                const pos = await getBrowserGeolocation();
                if (applyOriginIfValid(pos)) return;
            } catch {
                // continue with profile / country / session fallbacks
            }

            try {
                const resolved = await resolveMapFallBackCenterFromUserData({
                    userLatitude,
                    userLongitude,
                    defaultCountryId,
                    countries: countriesRef.current,
                    fetchCountriesList: () => dispatch(ServerListActions.getList('tblCountries')),
                });
                if (applyOriginIfValid(resolved)) return;
            } catch {
                // resolver already logs; continue
            }

            const last = companiesMapLastPositionRef.current;
            if (
                last &&
                isValidCoordinate(Number(last.lat), -90, 90) &&
                isValidCoordinate(Number(last.lng), -180, 180)
            ) {
                applyOriginIfValid({ lat: Number(last.lat), lng: Number(last.lng) });
                return;
            }

            applyOriginIfValid(getDefaultOrigin());
        })().finally(() => {
            if (!cancelled) setIsOriginReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [defaultCountryId, dispatch, isOpen, userLatitude, userLongitude]);

    useEffect(() => {
        return subscribe(LEADS_MODAL_ACCOUNT_CREATED, ({ rowId, creationDate }) => {
            setReviewRows((current) => markReviewRowAsCreated(current, rowId, creationDate));
            dispatch(EntityListActions.init(COMPANIES, false, null, null, null, true));
        });
    }, [dispatch]);

    const buckets = useMemo(() => splitByBucket(leads), [leads]);
    const activeLeads = useMemo(
        () => (activeBucket === RESULT_BUCKETS.DUPLICATES ? buckets.duplicates : buckets.new),
        [activeBucket, buckets.duplicates, buckets.new],
    );

    const duplicateBucketTabs = useMemo(
        () => [
            {
                key: RESULT_BUCKETS.NEW,
                title: `${getLiteral('label_leads_new')} (${buckets.new.length})`,
                content: <div className="fm-leads-modal__bucket-tab-spacer" />,
            },
            {
                key: RESULT_BUCKETS.DUPLICATES,
                title: `${getLiteral('label_leads_potential_duplicates')} (${buckets.duplicates.length})`,
                content: <div className="fm-leads-modal__bucket-tab-spacer" />,
            },
        ],
        [buckets.new.length, buckets.duplicates.length],
    );
    const selectedLeads = useMemo(() => getSelectedLeads(leads, selectedIds), [leads, selectedIds]);
    const selectedCost = selectedLeads.length;
    const safeCreditBalance = creditBalance ?? 0;
    const projectedBalance = safeCreditBalance - selectedCost;
    const canEnhance =
        selectedCost > 0 && creditBalance != null && creditBalance - selectedCost >= 0;
    const hasValidOrigin =
        origin != null &&
        isValidCoordinate(origin.lat, -90, 90) &&
        isValidCoordinate(origin.lng, -180, 180);
    const canRunPreview =
        hasValidOrigin &&
        isOriginReady &&
        isMapReady &&
        isFiniteNumber(radiusKm) &&
        Number(radiusKm) > 0 &&
        businessTypes.length > 0 &&
        !businessTypesLoading &&
        !businessTypesError;
    const categoryOptions = useMemo(() => {
        if (!businessTypes.length) return [];
        return [
            {
                label: getLiteral('label_leads_business_type'),
                options: [{ label: getLiteral('label_leads_all_business_types'), value: '' }],
            },
            ...businessTypes,
        ];
    }, [businessTypes]);
    const failedRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'failed'),
        [reviewRows],
    );
    const pendingReviewRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'pending'),
        [reviewRows],
    );
    const createdRows = useMemo(
        () => reviewRows.filter((row) => row.status === 'created'),
        [reviewRows],
    );
    const enrichmentBillableCount = useMemo(
        () => reviewRows.filter((row) => row.status !== 'failed').length,
        [reviewRows],
    );
    const creditsConfirmCopy = useMemo(() => {
        const balance = creditBalance ?? 0;
        const projected = creditBalance == null ? 0 : projectedBalance;
        const enhanceSummaryKey =
            selectedCost === 1
                ? 'label_leads_use_credits_enhance_summary_singular'
                : 'label_leads_use_credits_enhance_summary_plural';

        return {
            currentBalance: getLiteralWithParameters('label_leads_use_credits_current_balance', [
                balance,
            ]),
            enhanceSummary:
                selectedCost === 1
                    ? getLiteralWithParameters(enhanceSummaryKey, [selectedCost])
                    : getLiteralWithParameters(enhanceSummaryKey, [selectedCost, selectedCost]),
            failedNotCharged: getLiteral('label_leads_use_credits_failed_not_charged'),
            remainingBalance: getLiteralWithParameters(
                'label_leads_use_credits_remaining_balance',
                [projected],
            ),
        };
    }, [creditBalance, projectedBalance, selectedCost]);

    const userLang = getLang();
    const leadPopLiterals = useMemo(() => buildLeadPopLiterals(getLiteral, userLang), [userLang]);

    const reset = useCallback(() => {
        setStep(LEADS_MODAL_STEPS.SETUP);
        setCategory(DEFAULT_CATEGORY);
        setMaxResults(DEFAULT_MAX_RESULTS);
        setRadiusKm(DEFAULT_RADIUS_KM);
        setOrigin(null);
        setIsOriginReady(false);
        setInitialOrigin(null);
        setLeads([]);
        setSelectedIds([]);
        setActiveBucket(RESULT_BUCKETS.NEW);
        setCreditBalance(null);
        setBusinessTypes([]);
        setBusinessTypesLoading(false);
        setBusinessTypesError(false);
        setReviewRows([]);
        setProgress(getDefaultProgress());
        setEnrichmentReady(false);
        setBackendLeadQueue([]);
        setLoading(false);
        setError('');
        setIsMapReady(false);
        setConfirmCreditsOpen(false);
        resetLeadsPreviewSelection();
    }, [resetLeadsPreviewSelection]);

    const handleClose = useCallback(() => {
        beginClose();
        onClose();
    }, [beginClose, onClose]);

    const handleBackFromReviewToPreview = useCallback(() => {
        setStep(LEADS_MODAL_STEPS.PREVIEW);
        setError('');
        setConfirmCreditsOpen(false);
        setReviewRows([]);
        setEnrichmentReady(false);
    }, []);

    const handleViewEnrichmentResults = useCallback(() => {
        setEnrichmentReady(false);
        setStep(LEADS_MODAL_STEPS.REVIEW);
    }, []);

    const handleAfterClose = useCallback(() => {
        if (isOpenRef.current) return;
        LeadsService.cancelRequests();
        reset();
    }, [reset]);

    const runPreview = useCallback(() => {
        const originInvalid =
            !origin ||
            !isValidCoordinate(origin.lat, -90, 90) ||
            !isValidCoordinate(origin.lng, -180, 180) ||
            !isFiniteNumber(radiusKm) ||
            Number(radiusKm) <= 0;

        if (originInvalid) {
            setError(getLiteral('error_leads_valid_origin_required'));
            return;
        }

        if (businessTypesLoading) {
            return;
        }

        if (businessTypesError || !businessTypes.length) {
            setError(getLiteral('error_leads_business_types_load_failed'));
            return;
        }

        const requestVersion = requestVersionRef.current;
        setLoading(true);
        setError('');
        LeadsService.nearbyPlaces(
            buildPreviewRequest({
                category,
                maxResults,
                origin,
                radiusKm,
            }),
            origin,
        )
            .then((items) => {
                if (!isRequestCurrent(requestVersion)) return;
                setLeads(items);
                setSelectedIds([]);
                setActiveBucket(RESULT_BUCKETS.NEW);
                setStep(LEADS_MODAL_STEPS.PREVIEW);
                if (!items.length) setError(getLiteral('error_leads_no_coordinates_returned'));
            })
            .catch((requestError) => {
                if (
                    LeadsService.isCanceledRequest(requestError) ||
                    !isRequestCurrent(requestVersion)
                ) {
                    return;
                }
                setError(getLiteral('error_leads_preview_failed'));
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setLoading(false);
                }
            });
    }, [
        category,
        isRequestCurrent,
        maxResults,
        origin,
        radiusKm,
        businessTypes.length,
        businessTypesError,
        businessTypesLoading,
    ]);

    const toggleLead = useCallback((id) => {
        setSelectedIds((current) => {
            const isSelected = current.some((sid) => String(sid) === String(id));
            return isSelected
                ? current.filter((item) => String(item) !== String(id))
                : [...current, id];
        });
    }, []);

    const runEnrichment = useCallback(() => {
        const requestVersion = requestVersionRef.current;
        setConfirmCreditsOpen(false);
        setStep(LEADS_MODAL_STEPS.ENRICHING);
        setProgress({ current: 0, total: selectedLeads.length });
        setReviewRows([]);
        setEnrichmentReady(false);
        setBackendLeadQueue([]);
        setError('');
        setLoading(true);

        LeadsService.placeToAccountFormStream(selectedLeads, {
            onItem: (item, idx) => {
                if (!isRequestCurrent(requestVersion)) return;
                setReviewRows((prev) => [...prev, item]);
                setProgress({ current: idx + 1, total: selectedLeads.length });
                if (item?.status !== 'failed') {
                    setBackendLeadQueue((prev) => [
                        ...prev,
                        {
                            row: item,
                            key: `${item?.id ?? 'lead'}-${idx}`,
                        },
                    ]);
                }
            },
        })
            .then((items) => {
                if (!isRequestCurrent(requestVersion)) return;
                const enrichmentSuccessCount = items.filter(
                    (item) => item.status !== 'failed',
                ).length;
                setCreditBalance((current) =>
                    current != null ? current - enrichmentSuccessCount : current,
                );
                setReviewRows(items);
                LeadsService.getCredits()
                    .then(({ balance }) => {
                        if (!isRequestCurrent(requestVersion)) return;
                        setCreditBalance(balance);
                    })
                    .catch(() => {});
                setEnrichmentReady(true);
            })
            .catch((requestError) => {
                if (
                    LeadsService.isCanceledRequest(requestError) ||
                    !isRequestCurrent(requestVersion)
                ) {
                    return;
                }
                const partial = requestError?.partial;
                if (Array.isArray(partial) && partial.length) {
                    const enrichmentSuccessCount = partial.filter(
                        (item) => item.status !== 'failed',
                    ).length;
                    setCreditBalance((current) =>
                        current != null ? current - enrichmentSuccessCount : current,
                    );
                    const missing = selectedLeads.slice(partial.length).map((lead) => ({
                        id: String(lead.id),
                        sourceLead: lead,
                        status: 'failed',
                        error: getLiteral('error_leads_enhancement_failed'),
                    }));
                    setReviewRows([...partial, ...missing]);
                    LeadsService.getCredits()
                        .then(({ balance }) => {
                            if (!isRequestCurrent(requestVersion)) return;
                            setCreditBalance(balance);
                        })
                        .catch(() => {});
                    setEnrichmentReady(true);
                    return;
                }
                setError(getLiteral('error_leads_enhancement_failed'));
                setStep(LEADS_MODAL_STEPS.PREVIEW);
            })
            .finally(() => {
                if (isRequestCurrent(requestVersion)) {
                    setLoading(false);
                }
            });
    }, [isRequestCurrent, selectedLeads]);

    const handleReviewCreate = useCallback(
        (row) => {
            onReviewCreate(row, toAccountCrudData(row));
        },
        [onReviewCreate],
    );

    const renderLeadsMap = () => {
        if (!isOriginReady || !hasValidOrigin) {
            if (!isOriginReady) {
                return (
                    <div
                        className="fm-leads-modal__map fm-leads-modal__map--loading"
                        aria-busy="true"
                    >
                        <AbsoluteLoader size="large" />
                    </div>
                );
            }
            return null;
        }

        return (
            <LeadsMap
                activeLeads={activeLeads}
                selectedIds={selectedIds}
                origin={origin}
                initialOrigin={initialOrigin}
                radiusKm={radiusKm}
                onOriginChange={setOrigin}
                onToggleLead={toggleLead}
                onMapReadyChange={setIsMapReady}
            />
        );
    };

    const renderControls = () => (
        <div className="fm-leads-modal__controls">
            <div className="fm-leads-modal__controls-left">
                <div className="fm-leads-modal__search-place">
                    <PlaceSearch onSelectPlace={setOrigin} />
                </div>
                <LeadsQuickFilters
                    businessTypesError={businessTypesError}
                    businessTypesLoading={businessTypesLoading}
                    category={category}
                    categoryOptions={categoryOptions}
                    maxResults={maxResults}
                    maxResultOptions={MAX_RESULT_OPTIONS}
                    radiusKm={radiusKm}
                    radiusOptions={RADIUS_OPTIONS}
                    onChangeCategory={setCategory}
                    onChangeMaxResults={setMaxResults}
                    onChangeRadiusKm={setRadiusKm}
                />
            </div>
            <div className="fm-leads-modal__controls-right">
                <Button
                    type="primary"
                    isDisabled={!canRunPreview}
                    onClick={runPreview}
                    isLoading={loading}
                >
                    {getLiteral('action_leads_run_prospecting')}
                </Button>
            </div>
        </div>
    );

    const renderSetup = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--setup">
            <Advice type="info" showIcon>
                {getLiteral('label_leads_preview_is_free')}
            </Advice>
            {renderControls()}
            {renderLeadsMap()}
        </div>
    );

    const renderPreview = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--preview">
            {renderControls()}
            <div className="fm-leads-modal__split">
                {renderLeadsMap()}
                <div className="fm-leads-modal__list-column">
                    <Tabs
                        className="fm-leads-modal__bucket-tabs"
                        activeKey={activeBucket}
                        onChange={setActiveBucket}
                        tabs={duplicateBucketTabs}
                    />
                    <LeadList
                        activeLeads={activeLeads}
                        isDuplicatesEmpty={
                            activeBucket === RESULT_BUCKETS.DUPLICATES &&
                            buckets.duplicates.length === 0
                        }
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                    />
                </div>
            </div>
            <div className="fm-leads-modal__summary">
                <div className="fm-leads-modal__summary-metrics">
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral('label_leads_selected')}:</span>
                        <Text type="caption" bold as="span">
                            {selectedCost}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral('label_leads_current_balance')}:</span>
                        <Text type="caption" bold as="span">
                            {creditBalance == null ? EM_DASH : creditBalance}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral('label_leads_max_cost')}:</span>
                        <Text type="caption" bold as="span">
                            {selectedCost}
                        </Text>
                    </Text>
                    <Text type="caption" className="fm-text-inlineGap">
                        <span>{getLiteral('label_leads_projected_balance')}:</span>
                        <Text type="caption" bold as="span">
                            {creditBalance == null ? EM_DASH : projectedBalance}
                        </Text>
                    </Text>
                </div>
                {creditBalance != null && projectedBalance < 0 && (
                    <Text type="caption">{getLiteral('error_leads_insufficient_credits')}</Text>
                )}
                <Button
                    type="primary"
                    isDisabled={!canEnhance}
                    onClick={() => setConfirmCreditsOpen(true)}
                >
                    {getLiteral('action_leads_enhance_selected')}
                </Button>
            </div>
        </div>
    );

    const renderEnriching = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--enriching fm-leads-modal__progress fm-leads-modal__progress--with-game">
            <LeadPopEnrichingPanel
                literals={leadPopLiterals}
                sessionComplete={enrichmentReady}
                onViewProspectingResults={handleViewEnrichmentResults}
                enrichmentProgress={progress}
                backendLeadQueue={backendLeadQueue}
            />
            <div className="fm-leads-modal__enriching-status" aria-live="polite">
                <Text type="body" className="fm-text-inlineGap">
                    <span>{getLiteral('label_leads_enhancement_submitted')}:</span>
                    <span>{progress.current}</span>
                    <span>/</span>
                    <span>{progress.total}</span>
                </Text>
                <Progress value={progress.current} max={progress.total || 1} />
            </div>
        </div>
    );

    const renderReview = () => (
        <div className="fm-leads-modal__step fm-leads-modal__step--review">
            <div className="fm-leads-modal__review-summary">
                <ReviewSummary
                    readyCount={pendingReviewRows.length}
                    createdCount={createdRows.length}
                    failedCount={failedRows.length}
                    creditsUsed={enrichmentBillableCount}
                    balance={creditBalance == null ? EM_DASH : String(creditBalance)}
                />
                {pendingReviewRows.length > 0 && (
                    <Advice type="info" showIcon>
                        {getLiteral('label_leads_created_records_tip')}
                    </Advice>
                )}
            </div>
            <ReviewQueue rows={reviewRows} onReviewCreate={handleReviewCreate} />
            <div className="fm-leads-modal__footer-actions">
                <Button
                    type="secondary"
                    isDisabled={loading}
                    onClick={handleBackFromReviewToPreview}
                >
                    {getLiteral('action_leads_back_to_prospecting_results')}
                </Button>
                <Button type="primary" onClick={handleClose}>
                    {getLiteral('action_leads_finish')}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onAfterClose={handleAfterClose}
                onRequestClose={handleClose}
                size="full"
                title={getLiteral('action_leads_prospect_from_map')}
                useHeader
                classes={{ content: 'fm-leads-modal__content' }}
            >
                <div className="fm-leads-modal">
                    {(error || businessTypesError) && (
                        <Advice type="warning" showIcon>
                            {error || getLiteral('error_leads_business_types_load_failed')}
                        </Advice>
                    )}
                    {step === LEADS_MODAL_STEPS.SETUP && renderSetup()}
                    {step === LEADS_MODAL_STEPS.PREVIEW && renderPreview()}
                    {step === LEADS_MODAL_STEPS.ENRICHING && renderEnriching()}
                    {step === LEADS_MODAL_STEPS.REVIEW && renderReview()}
                </div>
            </Modal>
            <Modal
                isOpen={confirmCreditsOpen}
                title={getLiteral('title_leads_use_credits')}
                confirmText={getLiteral('action_leads_confirm_and_enhance')}
                cancelText={getLiteral('action_cancel')}
                onRequestClose={() => setConfirmCreditsOpen(false)}
                onCancel={() => setConfirmCreditsOpen(false)}
                onConfirm={runEnrichment}
            >
                <div className="fm-leads-modal__credits-confirm">
                    <Markdown
                        className="fm-leads-modal__credits-line"
                        content={creditsConfirmCopy.currentBalance}
                    />
                    <div className="fm-leads-modal__credits-block">
                        <Markdown
                            className="fm-leads-modal__credits-line"
                            content={creditsConfirmCopy.enhanceSummary}
                        />
                        <Markdown
                            className="fm-leads-modal__credits-line fm-leads-modal__credits-line--secondary"
                            content={creditsConfirmCopy.failedNotCharged}
                        />
                    </div>
                    <Markdown
                        className="fm-leads-modal__credits-line fm-leads-modal__credits-block"
                        content={creditsConfirmCopy.remainingBalance}
                    />
                </div>
            </Modal>
        </>
    );
};

export default memo(LeadsModal);
